/**
 * ContextualOfferCard — expandable "Do you qualify for a lower offer?"
 * mini-widget. Renders right below `AcademicFitPanel` on Cluster 2 · Applying.
 *
 * Data flow
 * ---------
 * Fetches from `/api/degrees/{id}/contextual-offer` which resolves in this
 * order: course-level override → uni-level baseline → null. When the API
 * returns null (no policy on this uni), the card renders nothing —
 * families never see a "not offered" placeholder.
 *
 * UX
 * --
 * Default state: a single-line teaser with the reduced offer and a
 * "Do you qualify?" button. Expanded: three-question form
 * (POLAR quintile, care leaver, first-generation) → each answer
 * updates the eligibility verdict inline. No submit button — the
 * answer changes live as you tick. Source URL is always shown so
 * families can confirm on the uni's own page.
 *
 * Storytelling angle
 * ------------------
 * WUA's contextual-offer feature is deliberately soft. We don't want
 * to guarantee ANYTHING — that's the uni's job on admission day. We
 * DO want to make it obvious that ~40% of state-school applicants
 * qualify for a reduced offer they may not have known about.
 *
 * Feb 2026 · Task 1 of the value-data batch.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ExternalLink, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Human-readable label for each eligibility flag — keeps the tooltip
// short and honest. Order below drives display order in the form.
const CRITERIA = [
  {
    key: "polar_quintiles",
    label: "I live in a POLAR Q1 or Q2 postcode",
    tooltip:
      "POLAR4 measures how likely young people in your postcode are to go to uni. Q1 = lowest participation. Check yours on the OfS POLAR lookup.",
    check: (val, elig) => Array.isArray(elig.polar_quintiles) && val,
  },
  {
    key: "care_experienced",
    label: "I've spent time in local-authority care",
    tooltip:
      "Includes foster care, kinship care, and residential care at any point since age 11.",
    check: (val, elig) => elig.care_experienced && val,
  },
  {
    key: "first_generation",
    label: "I'll be the first in my family to go to university",
    tooltip:
      "Neither parent nor primary carer holds a UK bachelor's or higher degree.",
    check: (val, elig) => elig.first_generation && val,
  },
];

export default function ContextualOfferCard({ degreeId }) {
  const [payload, setPayload] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (!degreeId) return;
    let cancelled = false;
    axios
      .get(`${API}/api/degrees/${degreeId}/contextual-offer`)
      .then((r) => !cancelled && setPayload(r.data))
      .catch(() => !cancelled && setPayload(null));
    return () => {
      cancelled = true;
    };
  }, [degreeId]);

  if (!payload?.policy) return null;
  const { policy, scope, university_name: uniName } = payload;
  const { grade, standard_grade, eligibility, notes, source_url } = policy;

  // Canonical A-level offer strings (AAA, AAB, ABB, A*AA, BCC, etc.).
  // Only these read naturally in the "offers a reduced X" copy — any
  // other value (prose like "Two grades below") gets a generic
  // headline and lets `notes` do the explaining.
  const gradeIsCanonical =
    typeof grade === "string" && /^[A-E*]{3,5}$/.test(grade.replace(/\s/g, ""));

  const headline = gradeIsCanonical ? (
    <>
      <b>{uniName || "This university"}</b> offers a reduced <b>{grade}</b>
      {standard_grade && gradeIsCanonical && standard_grade !== grade && (
        <> (down from {standard_grade})</>
      )}{" "}
      for applicants from under-represented backgrounds.
    </>
  ) : (
    <>
      <b>{uniName || "This university"}</b> offers a reduced entry offer
      for applicants from under-represented backgrounds — details below.
    </>
  );

  // Live verdict: any ticked criterion the uni honours → qualifies.
  const qualifies = CRITERIA.some((c) =>
    c.check(answers[c.key], eligibility || {}),
  );

  const anyAnswered = Object.values(answers).some(Boolean);

  return (
    <div
      data-testid="contextual-offer-card"
      className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50/60"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="contextual-offer-toggle"
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-indigo-50 transition-colors rounded-xl"
      >
        <div className="shrink-0 mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-900 text-indigo-50">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-uppercase !text-[10px] text-indigo-800">
            Contextual offer
          </div>
          <div className="mt-1 text-sm text-indigo-950 leading-snug">
            {headline}{" "}
            <span className="underline decoration-indigo-400 underline-offset-2">
              Do you qualify?
            </span>
          </div>
        </div>
        <ChevronDown
          className={`shrink-0 mt-1 w-4 h-4 text-indigo-700 transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <div
          className="px-4 pb-4 pt-1"
          data-testid="contextual-offer-form"
        >
          <div className="space-y-2.5 mt-1">
            {CRITERIA.map((c) => {
              // Skip criteria this uni doesn't honour so the form stays honest.
              const uniHonoursThis = (() => {
                if (c.key === "polar_quintiles")
                  return Array.isArray(eligibility?.polar_quintiles);
                return !!eligibility?.[c.key];
              })();
              if (!uniHonoursThis) return null;
              return (
                <label
                  key={c.key}
                  className="flex items-start gap-2.5 cursor-pointer group"
                  data-testid={`contextual-offer-criterion-${c.key}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-indigo-700"
                    checked={!!answers[c.key]}
                    onChange={(e) =>
                      setAnswers((prev) => ({
                        ...prev,
                        [c.key]: e.target.checked,
                      }))
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-stone-800 leading-snug">
                      {c.label}
                    </div>
                    <div className="text-[11px] text-stone-600 mt-0.5 leading-snug">
                      {c.tooltip}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {anyAnswered && (
            <div
              className={`mt-3 rounded-md px-3 py-2 text-sm font-medium ${
                qualifies
                  ? "bg-emerald-100 text-emerald-900"
                  : "bg-amber-100 text-amber-900"
              }`}
              data-testid="contextual-offer-verdict"
            >
              {qualifies ? (
                <>
                  <b>You likely qualify.</b>{" "}
                  {gradeIsCanonical ? (
                    <>The reduced offer is <b>{grade}</b>.</>
                  ) : (
                    <>Check the notes below for how much the offer drops.</>
                  )}{" "}
                  Confirm with {uniName || "the uni"}'s admissions office when you apply.
                </>
              ) : (
                <>
                  You don't qualify on the boxes ticked above — but many
                  unis have additional pathways (school-based programmes,
                  refugee status, young carer). Check the source below.
                </>
              )}
            </div>
          )}

          {notes && (
            <p
              className="mt-3 text-xs text-stone-700 leading-relaxed"
              data-testid="contextual-offer-notes"
            >
              <b>Small print:</b> {notes}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-stone-600">
            {source_url && (
              <a
                href={source_url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="contextual-offer-source"
                className="inline-flex items-center gap-1 hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                Read the full policy at {uniName || "the uni"}
              </a>
            )}
            <span className="text-stone-500">
              {scope === "course"
                ? "Course-specific policy"
                : "University-wide policy"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
